import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Col, Form, Row, Spinner, Table } from 'react-bootstrap';
import airbnbApi from './api';
import { ErrorAlert, Notice, PageLoader, formatTime } from './common';

const STATUS_VARIANTS = { pending: 'warning', processed: 'success', failed: 'danger', ignored: 'secondary' };

// NextPax Notifications API (webhooks): subscription status, subscribe /
// unsubscribe for booking events, and the latest events received.
const NotificationSettings = () => {
  const [data, setData] = useState(null);
  const [events, setEvents] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const loadSubscription = useCallback(() => {
    airbnbApi.notificationSubscriptions().then(setData).catch(setError);
  }, []);

  const loadEvents = useCallback(() => {
    airbnbApi.notificationEvents({ status: statusFilter || undefined, limit: 50 }).then(setEvents).catch(setError);
  }, [statusFilter]);

  useEffect(() => { loadSubscription(); }, [loadSubscription]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const subscribe = () => {
    setBusy('subscribe');
    setError(null);
    airbnbApi.subscribeNotifications('bookings')
      .then(result => { setNotice(`Subscribed to booking notifications (request ${result.request_id}).`); loadSubscription(); })
      .catch(setError)
      .finally(() => setBusy(null));
  };

  const unsubscribe = () => {
    if (!window.confirm('Stop receiving booking notifications from NextPax? Polling must then run every minute.')) return;
    setBusy('unsubscribe');
    setError(null);
    airbnbApi.unsubscribeNotifications('bookings')
      .then(() => { setNotice('Unsubscribed from booking notifications.'); loadSubscription(); })
      .catch(setError)
      .finally(() => setBusy(null));
  };

  const config = data && data.config;
  const subscriptions = (data && data.subscriptions) || [];
  const bookingSubscription = subscriptions.find(s => s.notificationSubType === 'booking');
  const configErrors = (config && config.configuration_errors) || [];

  return (
    <div className="container-fluid pad20 text-start">
      <div className="margbot10 fnt13"><Link to="/airbnb">Property managers</Link> / Notifications</div>
      <h4>NextPax booking notifications</h4>
      <p className="fnt13 text-muted">
        NextPax calls RedChannel's webhook when an Airbnb booking is created, modified or cancelled. Each event only
        says which booking changed; RedChannel then fetches the booking from the Bookings API. Polling
        <code> GET /bookings</code> must still run at least every 30 minutes — NextPax drops events if the webhook
        keeps failing.
      </p>
      <ErrorAlert error={error} onClose={() => setError(null)} />
      <Notice notice={notice} onClose={() => setNotice(null)} />

      {!data && !error && <PageLoader />}
      {data && (
        <Card className="margbot15">
          <Card.Header className="bld d-flex justify-content-between align-items-center">
            <span>
              Subscription{' '}
              {bookingSubscription
                ? <Badge bg="success">active</Badge>
                : <Badge bg="secondary">not subscribed</Badge>}
            </span>
            <span>
              {bookingSubscription ? (
                <Button size="sm" variant="outline-danger" disabled={!!busy} onClick={unsubscribe}>
                  {busy === 'unsubscribe' ? <Spinner animation="border" size="sm" /> : 'Unsubscribe'}
                </Button>
              ) : (
                <Button size="sm" disabled={!!busy || configErrors.length > 0} onClick={subscribe}>
                  {busy === 'subscribe' ? <Spinner animation="border" size="sm" /> : 'Subscribe to booking notifications'}
                </Button>
              )}
            </span>
          </Card.Header>
          <Card.Body>
            {data.status_error && (
              <Alert variant="warning" className="fnt13">
                Could not read the subscription from NextPax: {data.status_error.message}
                {data.status_error.code && ` (${data.status_error.code})`}. NextPax support must enable the
                Notifications API for the account first.
              </Alert>
            )}
            {configErrors.length > 0 && (
              <Alert variant="warning" className="fnt13">
                <div className="bld">RedChannel isn't configured to subscribe yet:</div>
                <ul className="mb-0">{configErrors.map(e => <li key={e}>{e}</li>)}</ul>
              </Alert>
            )}
            <Row className="fnt13">
              <Col md={6} className="margbot10">
                <div className="text-muted fnt12">Webhook URL (RedChannel)</div>
                <code>{(bookingSubscription && bookingSubscription.webhookUrl) || config.webhook_url || '—'}</code>
              </Col>
              <Col md={2} className="margbot10">
                <div className="text-muted fnt12">Webhook auth</div>
                {(bookingSubscription && bookingSubscription.authenticationType) || config.authentication_type || '—'}
              </Col>
              <Col md={2} className="margbot10">
                <div className="text-muted fnt12">Version</div>
                {(bookingSubscription && bookingSubscription.webhookVersion) || config.webhook_version}
              </Col>
              <Col md={2} className="margbot10">
                <div className="text-muted fnt12">Subscribed since</div>
                {bookingSubscription ? formatTime(bookingSubscription.createdAt) : '—'}
              </Col>
              <Col md={12} className="fnt12 text-muted">NextPax Notifications API: {config.notifications_url}</Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      <Card className="margbot15">
        <Card.Header className="bld d-flex justify-content-between align-items-center">
          <span>
            Received events{' '}
            {events && Object.entries(events.counts || {}).map(([status, n]) => (
              <Badge key={status} bg={STATUS_VARIANTS[status] || 'secondary'} className="ms-1">{status}: {n}</Badge>
            ))}
          </span>
          <span className="d-flex align-items-center gap-2">
            <Form.Select size="sm" style={{ width: 140 }} value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              {Object.keys(STATUS_VARIANTS).map(s => <option key={s} value={s}>{s}</option>)}
            </Form.Select>
            <Button size="sm" variant="outline-secondary" onClick={loadEvents}>Refresh</Button>
          </span>
        </Card.Header>
        <Card.Body>
          {!events && <PageLoader />}
          {events && events.events.length === 0 && <div className="text-muted">No events received yet.</div>}
          {events && events.events.length > 0 && (
            <Table bordered size="sm" className="bg1 align-middle mb-0 fnt13">
              <thead>
                <tr>
                  <th>Received</th><th>Event</th><th>Booking state</th><th>Property</th>
                  <th>NextPax booking</th><th>Channel reference</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {events.events.map(e => (
                  <tr key={e.id}>
                    <td className="fnt12">{formatTime(e.received_at)}</td>
                    <td>{e.sub_type}{e.channel_id ? ` · ${e.channel_id}` : ''}</td>
                    <td>{e.booking_state || '—'}</td>
                    <td>
                      {e.airbnb_property_id
                        ? <Link to={`/airbnb/properties/${e.airbnb_property_id}`}>{e.property_name}</Link>
                        : <span className="fnt12 text-muted">{e.property_id || '—'}</span>}
                    </td>
                    <td className="fnt12">{e.next_pax_reference || '—'}</td>
                    <td className="fnt12">{e.channel_partner_reference || '—'}</td>
                    <td>
                      <Badge bg={STATUS_VARIANTS[e.status] || 'secondary'}>{e.status}</Badge>
                      {e.error_message && <div className="fnt12 text-danger">{e.error_message}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
};

export default NotificationSettings;
